import { getApiUrl } from "../utils/envConfig";
import { makeAuthenticatedRequest } from "./authApi";
import { getAuthToken } from "../utils/authUtils";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type CreateInviteLinkRequest = components["schemas"]["CreateInviteLinkRequest"];
type InviteLinkPublicInfo = components["schemas"]["InviteLinkPublicInfo"];
type RegisterViaLinkRequest = components["schemas"]["RegisterViaLinkRequest"];
type RegisterViaLinkResponse =
  components["schemas"]["RegisterViaLinkResponse"];
type RequestNewInvitationResponse =
  components["schemas"]["RequestNewInvitationResponse"];

export const createInviteLink = async ({ id_client, role, product_scopes, domain_restriction }: CreateInviteLinkRequest) => {
  return makeAuthenticatedRequest(`${getApiUrl()}/invite-links`, {
    method: "POST",
    body: JSON.stringify({ id_client, role, product_scopes, domain_restriction }),
  });
};

export const getInviteLink = async (token: string): Promise<InviteLinkPublicInfo> => {
  const res = await fetch(`${getApiUrl()}/invite-links/${token}`);
  if (!res.ok) throw new Error("Failed to fetch invite link");
  return res.json();
};

export const registerViaLink = async (token: string, { name, email, password }: RegisterViaLinkRequest): Promise<RegisterViaLinkResponse> => {
  const res = await fetch(`${getApiUrl()}/invite-links/${token}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail || "Registration failed");
  return data;
};

/**
 * Request a fresh invitation link for an account that didn't activate in time.
 *
 * Public endpoint — no auth required. The backend returns the same generic
 * 200 response regardless of whether the email is registered, pending, or
 * already activated (enumeration defence), so callers should never branch
 * on response content. The only failure mode the caller needs to handle is
 * HTTP 429 (rate limited).
 *
 * @param {object} args
 * @param {string} args.email - Email address to send the new invitation to.
 * @returns {Promise<{success: boolean, message: string}>}
 * @throws {Error} with `.status` when the response is non-OK (most commonly
 *   429 "rate limited" — message includes the Retry-After window).
 */
export const requestNewInvitation = async ({ email }: { email: string }): Promise<RequestNewInvitationResponse> => {
  const res = await fetch(`${getApiUrl()}/users/request-new-invitation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.detail || "";
    } catch {
      // body wasn't JSON
    }
    const err: any = new Error(
      detail || `Failed to request a new invitation (HTTP ${res.status}).`,
    );
    err.status = res.status;
    throw err;
  }
  return res.json();
};

/**
 * Resend an invitation as an authenticated workspace admin.
 *
 * Mirrors `requestNewInvitation` but targets the audited admin endpoint.
 * Calls `POST /users/admin/resend-invitation` with the caller's
 * Firebase Bearer token; the backend records who triggered the resend
 * (workspace_admin vs dashmote_staff) in the invitation audit log. The
 * customer-facing `/invitation-expired` page must keep calling
 * `requestNewInvitation` (anonymous), since end users do not hold a
 * workspace-admin token at that point.
 *
 * Same response shape and same 429 rate-limit semantics as the public route.
 *
 * @param {object} args
 * @param {string} args.email - Email address to resend the invitation to.
 * @param {"email"|"copy"} [args.delivery="email"] - "email" mails the fresh
 *   link; "copy" skips the email and returns it in `invitation_link` for the
 *   admin to share manually.
 * @returns {Promise<{success: boolean, message: string, invitation_link?: string | null}>}
 * @throws {Error} with `.status` on non-OK responses (e.g. 429 rate-limited,
 *   403 if the caller lacks workspace-admin role).
 */
export const adminResendInvitation = async ({
  email,
  delivery = "email",
}: {
  email: string;
  delivery?: "email" | "copy";
}): Promise<RequestNewInvitationResponse> => {
  // Manually attach the Firebase Bearer token rather than going through
  // `makeAuthenticatedRequest` — the latter throws a plain Error on 4xx
  // without exposing `error.status`, which would break the
  // `handleResendInvitation` 429 rate-limit branch in UserManagementPage.
  // This shape matches `requestNewInvitation` above so both callers handle
  // errors identically.
  const token = await getAuthToken();
  const res = await fetch(`${getApiUrl()}/users/admin/resend-invitation`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ email, delivery }),
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.detail || "";
    } catch {
      // body wasn't JSON
    }
    const err: any = new Error(
      detail || `Failed to resend invitation (HTTP ${res.status}).`,
    );
    err.status = res.status;
    throw err;
  }
  return res.json();
};
